import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { CoralPin } from "@/components/branding/CoralPin";
import { Loader2, RefreshCw, CalendarClock } from "lucide-react";

interface Props {
  reason: "admin" | "outage" | null;
  expectedDate?: string;
  ceoName?: string;
  message?: string;
  checking?: boolean;
  onRetry: () => void;
}

export function MaintenanceScreen({ reason, expectedDate, ceoName, message, checking, onRetry }: Props) {
  const { t } = useTranslation();

  const title = t("maintenance.title", "Estamos a melhorar a BeraStays");
  const subtitle =
    message ||
    (reason === "outage"
      ? t("maintenance.outageDesc", "Os nossos serviços estão temporariamente indisponíveis. Voltamos automaticamente assim que tudo estiver operacional.")
      : t("maintenance.adminDesc", "A plataforma está em manutenção programada. Voltamos muito em breve."));

  return (
    <div className="fixed inset-0 z-[99999] overflow-y-auto bg-background">
      <div
        className="min-h-full w-full flex items-center justify-center px-5 py-12"
        style={{ background: "radial-gradient(1200px 600px at 50% -10%, hsl(var(--primary) / 0.14), transparent 70%)" }}
      >
        <div className="w-full max-w-md text-center">
          <div className="mx-auto mb-7 flex h-24 w-24 items-center justify-center rounded-3xl bg-card shadow-[0_18px_50px_-18px_hsl(var(--primary)/0.55)] ring-1 ring-primary/15">
            <CoralPin className="h-14 w-14" />
          </div>

          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-medium uppercase tracking-wider text-primary">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            {t("maintenance.badge", "Manutenção")}
          </span>

          <h1 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight text-foreground">{title}</h1>
          <p className="mt-3 text-sm sm:text-base leading-relaxed text-muted-foreground">{subtitle}</p>

          {expectedDate && (
            <div className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-border/60 bg-card px-4 py-2.5 text-sm text-foreground">
              <CalendarClock className="h-4 w-4 text-primary" />
              <span>
                {t("maintenance.expected", "Previsão de retorno")}: <strong>{expectedDate}</strong>
              </span>
            </div>
          )}

          <div className="mt-8">
            <Button onClick={onRetry} disabled={checking} className="h-11 rounded-full px-7">
              {checking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {t("maintenance.retry", "Tentar novamente")}
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">
              {t("maintenance.autoCheck", "Estamos a verificar automaticamente a cada 20 segundos.")}
            </p>
          </div>

          <div className="mt-10 border-t border-border/50 pt-5 text-xs text-muted-foreground">
            {ceoName && (
              <p className="mb-1">
                {t("maintenance.signedBy", "Equipa BeraStays")} — <strong className="text-foreground">{ceoName}</strong>
              </p>
            )}
            <p>© {new Date().getFullYear()} BeraStays</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MaintenanceScreen;
